import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComplianceCoach from "@/components/ComplianceCoach";
import { Plus, ArrowRight, ShieldCheck, Zap, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Draft", "ImpactAssessment", "Review", "Approved", "Implementation", "Closed"];

export default function ChangeControl() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", change_type: "Design", reason: "", risk_level: "Medium", linked_risk_id: "" });
  const [risks, setRisks] = useState([]);
  const [impactOpen, setImpactOpen] = useState(false);
  const [impact, setImpact] = useState({ quality_impact: "", regulatory_impact: "", validation_impact: "", training_impact: "", estimated_effort_days: 5 });
  const [esignOpen, setEsignOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = async () => setList((await api.get("/changes")).data);
  useEffect(() => {
    load();
    // Preload the ISO 14971 risk register so users can link a change to a
    // specific risk record when raising it. Assistive link — not a proof of
    // impact adequacy.
    api.get("/risk/entries").then(({ data }) => setRisks(data || [])).catch(() => {});
  }, []);
  const loadDetail = async (id) => setDetail((await api.get(`/changes/${id}`)).data);

  const create = async () => {
    if (!form.title || !form.reason) return toast.error("Title and reason required");
    try {
      await api.post("/changes", form);
      // If the user picked a risk, also record a traceability spine link
      // (change → risk, relation='impacts') so the KPI dashboard reflects it.
      if (form.linked_risk_id) {
        try {
          await api.post("/traces", {
            project_id: risks.find((r) => r.risk_id === form.linked_risk_id)?.project_id,
            source_type: "change", source_id: "pending",
            target_type: "risk", target_id: form.linked_risk_id,
            relation: "impacts",
            notes: `Change: ${form.title}`,
          });
        } catch { /* trace is best-effort */ }
      }
      toast.success("Change Control opened");
      setOpen(false);
      setForm({ title: "", description: "", change_type: "Design", reason: "", risk_level: "Medium", linked_risk_id: "" });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && e.response?.status !== 403 && e.response?.status < 500) {
        toast.error(typeof detail === "string" ? detail : "Create failed");
      }
    }
  };

  const submitImpact = async () => {
    await api.post(`/changes/${detail.change_id}/impact`, impact);
    toast.success("Impact assessment saved (moved to Review)");
    setImpactOpen(false);
    loadDetail(detail.change_id);
    setRefreshKey((k) => k + 1);
  };

  const requestTransition = (toState) => {
    setPending({ toState });
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    await api.post(`/changes/${detail.change_id}/transition`, {
      to_state: pending.toState, signature_id: sig.signature_id, notes: sig.reason,
    });
    toast.success(`Advanced to ${pending.toState}`);
    setPending(null);
    loadDetail(detail.change_id);
    load();
    setRefreshKey((k) => k + 1);

  // Push the current change (must be Approved / Implementation / Closed) into
  // the Notified-Body portfolio as an immutable snapshot. Idempotent on the
  // backend so double-clicks don't duplicate deltas. Toast links to the new
  // delta so QA can jump straight to it.
  };

  const pushToNBPortfolio = async () => {
    if (!detail?.change_id) return;
    try {
      const { data } = await api.post(`/changes/${detail.change_id}/nb-portfolio/push`);
      toast.success(
        data.reused
          ? `Re-used existing delta ${data.delta_id} — nothing to push`
          : `Delta ${data.delta_id} published to NB portfolio · ${(data.risks || []).length} risks, ${(data.documents || []).length} docs`,
        {
          action: {
            label: "Open portfolio",
            onClick: () => { window.location.href = "/audit/nb-portfolio"; },
          },
        },
      );
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push to NB failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">Quality Events</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Change Control</h1>
            <p className="text-slate-600 mt-2">Design / process / documentation change management</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="chg-new-btn" className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New Change
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">All Changes ({list.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((c) => (
                <button key={c.change_id} data-testid={`chg-row-${c.change_id}`}
                        onClick={() => loadDetail(c.change_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${detail?.change_id === c.change_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-slate-100 text-slate-700">{c.change_type}</span>
                    <span className="text-[10px] font-mono text-slate-500">{c.state}</span>
                  </div>
                  <div className="font-semibold text-sm text-slate-900 truncate">{c.title}</div>
                  <div className="text-[10px] font-mono text-slate-400">{c.change_id}</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No changes.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <>
                <div className="vq-card p-6 mb-4">
                  <div className="vq-overline mb-1 font-mono">{detail.change_id}</div>
                  <h2 className="font-display font-bold text-2xl text-slate-900">{detail.title}</h2>
                  <p className="text-slate-600 mt-2 mb-4">{detail.description}</p>
                  <div className="text-xs mb-4">
                    <span className="text-slate-500 font-mono uppercase tracking-wider">Reason:</span> {detail.reason}
                  </div>

                  <div className="flex items-center flex-wrap gap-2 mb-4">
                    {STATES.map((s, i) => {
                      const idx = STATES.indexOf(detail.state);
                      return (
                        <div key={s} className="flex items-center gap-2">
                          <span data-testid={`chg-state-${s}`} className={`px-2 py-1 rounded text-[10px] font-mono ${
                            s === detail.state ? "bg-slate-900 text-white" : i < idx ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                          }`}>{s}</span>
                          {i < STATES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    {detail.state === "Draft" && (
                      <Button onClick={() => setImpactOpen(true)} data-testid="chg-impact-btn"
                              className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <Zap className="w-4 h-4 mr-2" /> Perform Impact Assessment
                      </Button>
                    )}
                    {STATES.indexOf(detail.state) >= 2 && detail.state !== "Closed" && (
                      <Button onClick={() => requestTransition(STATES[STATES.indexOf(detail.state) + 1])}
                              data-testid="chg-transition-btn"
                              className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Advance — with e-sig
                      </Button>
                    )}
                    {["Approved", "Implementation", "Closed"].includes(detail.state) && (
                      <Button onClick={pushToNBPortfolio}
                              variant="outline"
                              data-testid="chg-push-nb-btn"
                              title="Snapshot this approved change + linked risks + linked SOP versions into an audit-ready NB portfolio delta.">
                        <Building2 className="w-4 h-4 mr-2" /> Push to NB portfolio
                      </Button>
                    )}
                  </div>
                </div>

                {detail.impact_assessment && (
                  <div className="vq-card p-6 mb-4">
                    <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Impact Assessment</h3>
                    <div className="space-y-2 text-sm">
                      {Object.entries(detail.impact_assessment).map(([k, v]) => (
                        <div key={k} className="flex gap-4 border-b border-slate-100 pb-2">
                          <div className="w-40 text-xs font-mono uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                          <div className="flex-1 text-slate-900">{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(detail.approvers || []).length > 0 && (
                  <div className="vq-card p-6 mb-4">
                    <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Approvers</h3>
                    <div className="space-y-1">
                      {detail.approvers.map((a, i) => (
                        <div key={`${a.email}-${a.at || i}`} className="text-xs font-mono flex justify-between">
                          <span>{a.email} — {a.role}</span>
                          <span className="text-slate-500">{new Date(a.at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <AuditTrailPanel entityType="change" entityId={detail.change_id} refreshKey={refreshKey} />
              </>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a change control.</div>
            )}
          </div>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New Change Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input value={form.title} data-testid="chg-form-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Description</Label>
              <Textarea value={form.description} data-testid="chg-form-desc" onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Reason for change</Label>
              <Textarea value={form.reason} data-testid="chg-form-reason" onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Type</Label>
                <Select value={form.change_type} onValueChange={(v) => setForm({ ...form, change_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Design", "Process", "Software", "Supplier", "Documentation"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
              <div><Label className="text-xs font-semibold">Risk</Label>
                <Select value={form.risk_level} onValueChange={(v) => setForm({ ...form, risk_level: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Medium", "High"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Link to ISO 14971 risk (optional)</Label>
              <Select value={form.linked_risk_id || "none"} onValueChange={(v) => setForm({ ...form, linked_risk_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="chg-form-linked-risk"><SelectValue placeholder="Pick a risk from the register" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {risks.slice(0, 100).map((r) => (
                    <SelectItem key={r.risk_id} value={r.risk_id}>
                      {(r.hazard || "").slice(0, 60)} • RPN {r.rpn ?? "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Creates a &lsquo;change → risk&rsquo; link in the traceability spine. Assistive only.</p>
            </div>
            <ComplianceCoach entityType="change" draft={form} testIdPrefix="chg-coach" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="chg-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impact assessment dialog */}
      <Dialog open={impactOpen} onOpenChange={setImpactOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Impact Assessment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {["quality_impact", "regulatory_impact", "validation_impact", "training_impact"].map((k) => (
              <div key={k}>
                <Label className="text-xs font-semibold capitalize">{k.replace(/_/g, " ")}</Label>
                <Textarea data-testid={`chg-impact-${k}`} value={impact[k]} onChange={(e) => setImpact({ ...impact, [k]: e.target.value })} rows={2} className="mt-1.5" />
              </div>
            ))}
            <div><Label className="text-xs font-semibold">Estimated effort (days)</Label>
              <Input type="number" value={impact.estimated_effort_days}
                     onChange={(e) => setImpact({ ...impact, estimated_effort_days: parseInt(e.target.value) || 0 })}
                     className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpactOpen(false)}>Cancel</Button>
            <Button onClick={submitImpact} data-testid="chg-impact-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Save & advance to Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pending && (
        <ESignatureDialog
          open={esignOpen} onOpenChange={setEsignOpen}
          entityType="change" entityId={detail?.change_id}
          action={`change_transition_${pending.toState}`}
          defaultMeaning={pending.toState === "Approved" ? "Approval" : "Review"}
          title={`Sign to advance to ${pending.toState}`}
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}
